import { VeralliaLine } from './verallia-components/VeralliaLine';
// import { VeralliaEventsHistory } from './verallia-components/VeralliaEventsHistory';
// import { getInfluxdbData } from '../../services/api/influxdb-tools';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useSocket } from '../../contexts/SocketContext';

const HEARTBEAT_TIMEOUT_MS = 20000;

const HEARTBEAT_BADGES = {
  waiting: { label: 'Esperando datos', color: 'border-neutral-500/40 bg-neutral-500/10 text-neutral-300', dot: 'bg-neutral-400' },
  loading: { label: 'Inicializando', color: 'border-amber-500/40 bg-amber-500/10 text-amber-400', dot: 'bg-amber-400' },
  live: { label: 'Conectado', color: 'border-green-500/40 bg-green-500/10 text-green-400', dot: 'bg-green-400' },
  unavailable: { label: 'Sin conexión', color: 'border-red-500/40 bg-red-500/10 text-red-400', dot: 'bg-red-400' }
};

/*
const formatEventsHistoryData = (data) => data.filter((event) => event.detected).map((event) => ({
  id: `${event.pallet_id}-${event.client_id}-${event.time}-${event.face}-${event.defect_type}`,
  line: parseInt(event.line.replace('line_', '')),
  pallet_id: event.pallet_id,
  client_id: event.client_id,
  face: event.face === 'null' || event.face === null ? null : parseInt(event.face),
  defect_type: parseInt(event.defect_type),
  time: Math.floor(new Date(event.time).getTime() / 1000)
}));
*/

export const VeralliaLivePage = () => {
  const { screen } = useParams();
  const screenId = screen ? parseInt(screen) : 1;

  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();
  const [pallets, setPallets] = useState([]);
  const [engineStatus, setEngineStatus] = useState(null);
  const [isHeartbeatExpired, setIsHeartbeatExpired] = useState(false);
  const lastHeartbeatRef = useRef(Date.now());

  /*
  const [events, setEvents] = useState([]);
  const [isEventsLoading, setIsEventsLoading] = useState(true);

  const updateEventsHistory = async () => {
    setIsEventsLoading(true);

    try {
      const eventsData = await getInfluxdbData('engine', 'pallet_defect', [], [], '-1w', 'now()', '1ms', null, null, [], null, null, false, 'desc');
      setEvents(formatEventsHistoryData(eventsData));
    } catch (error) {
      console.error('Unable to load Verallia events history:', error);
      setEvents([]);
    } finally {
      setIsEventsLoading(false);
    }
  };

  useEffect(() => {
    updateEventsHistory();
  }, []);
  */

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setIsHeartbeatExpired(Date.now() - lastHeartbeatRef.current >= HEARTBEAT_TIMEOUT_MS);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const handleReceivedData = (data) => {
      if (data?.pallets !== undefined) setPallets(Array.isArray(data.pallets) ? data.pallets : []);

      if (data?.engine_status === 0 || data?.engine_status === 1) {
        lastHeartbeatRef.current = Date.now();
        setEngineStatus(data.engine_status);
        setIsHeartbeatExpired(false);
      }
    };

    subscribeToEvent('current_data', handleReceivedData);
    return () => unsubscribeFromEvent('current_data', handleReceivedData);
  }, [socket, subscribeToEvent, unsubscribeFromEvent]);

  const heartbeatState = isHeartbeatExpired ? 'unavailable' : engineStatus === 0 ? 'loading' : engineStatus === 1 ? 'live' : 'waiting';
  const heartbeatBadge = HEARTBEAT_BADGES[heartbeatState];
  const showOverlay = heartbeatState === 'unavailable' || heartbeatState === 'loading';

  const line1Pallets = pallets.filter((pallet) => pallet.line === 'line_1');
  const line2Pallets = pallets.filter((pallet) => pallet.line === 'line_2');
  const line3Pallets = pallets.filter((pallet) => pallet.line === 'line_3');

  return (
    <div className="relative h-full p-4">
      <div className="pointer-events-none fixed left-1/2 top-0 z-[100] flex h-8 -translate-x-1/2 items-center justify-center">
        <div role="status" className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-0.5 text-sm font-semibold ${heartbeatBadge.color}`}>
          <span className={`size-2.5 shrink-0 rounded-full ${heartbeatBadge.dot}`} />
          {heartbeatBadge.label}
        </div>
      </div>

      <div className="h-full min-h-0 w-full">
        <div className="flex h-full min-h-0 flex-col justify-center gap-4">
          {screenId === 1 ? (
            <>
              <div className="min-h-0 flex-1">
                <VeralliaLine lineId={1} pallets={line1Pallets} />
              </div>

              <div className="min-h-0 flex-1">
                <VeralliaLine lineId={2} pallets={line2Pallets} />
              </div>
            </>
          ) : (
            <div className="min-h-0 flex-1">
              <VeralliaLine lineId={3} pallets={line3Pallets} />
            </div>
          )}
        </div>
      </div>

      {showOverlay && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 p-6 backdrop-blur-sm">
          <div role="alert" className="flex w-full max-w-3xl flex-col items-center gap-6 rounded-2xl border border-white/10 bg-perception-black-700 px-10 py-12 text-center text-white shadow-2xl">
            {isHeartbeatExpired ? <ExclamationTriangleIcon className="size-24 text-red-400" /> : <ArrowPathIcon className="size-24 animate-spin text-amber-400" />}
            <div className="text-4xl font-extrabold">{isHeartbeatExpired ? 'Sistema no disponible' : 'Inicializando el sistema'}</div>
            <div className="text-2xl font-medium text-neutral-300">
              {isHeartbeatExpired ? 'El sistema no está disponible y se reiniciará próximamente.' : 'El sistema se está inicializando y estará disponible muy pronto.'}
            </div>
          </div>
        </div>
      )}

      {/*
      <div className="min-h-0 overflow-y-auto portrait:flex-1 portrait:w-full landscape:h-full landscape:w-1/4">
        <VeralliaEventsHistory events={events} isLoading={isEventsLoading} />
      </div>
      */}
    </div>
  );
};